/**
 * 🛡️ Backup & Secure Archive Center
 * -----------------------------------------------------------
 * Professional, password-protected backup management for Sanhaja.
 * - Modern dashboard with live stats
 * - Create / download / delete backups (super_admin only)
 * - Per-file SHA-256 integrity check on download
 * - Audit log
 */
import React, { useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Shield, Database, Download, Trash2, Lock, AlertTriangle, RefreshCw,
  Archive, KeyRound, FileCheck, History, HardDrive, Plus, ShieldAlert,
} from 'lucide-react';
import { AuthContext, API, formatDateWithEnglishNumerals } from '../shared';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtSize = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(2)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const TYPE_BADGES = {
  full: { label: 'كاملة', color: 'bg-brand-100 text-brand-800', icon: '💾' },
  season: { label: 'موسم', color: 'bg-purple-100 text-purple-800', icon: '📅' },
  financial: { label: 'مالية', color: 'bg-green-100 text-green-800', icon: '💰' },
  archive_export: { label: 'أرشيف', color: 'bg-gray-200 text-gray-700', icon: '🗄️' },
};

const SecureBackupCenter = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [backups, setBackups] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    backup_type: 'full',
    password: '',
    password2: '',
    season: '',
    notes: '',
  });

  const [seasons, setSeasons] = useState([]);

  // Delete confirm
  const [confirmDel, setConfirmDel] = useState({ open: false, filename: '' });

  // Permission gate
  const isAdmin = user?.role === 'super_admin';

  const fetchAll = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [s, b, a, sList] = await Promise.all([
        axios.get(`${API}/backup/dashboard-stats`, auth()),
        axios.get(`${API}/backup/list`, auth()),
        axios.get(`${API}/backup/audit-log?limit=80`, auth()),
        axios.get(`${API}/lifecycle/seasons`, auth()).catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setBackups(b.data || []);
      setAudit(a.data || []);
      setSeasons(sList.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!isAdmin) {
    return (
      <div className="p-6" dir="rtl">
        <Card>
          <CardContent className="p-12 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-rose-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-800">⛔ هذه الصفحة للمدير العام فقط</h2>
            <p className="text-gray-500 mt-2">إنشاء وتحميل وحذف النسخ الاحتياطية محصور بأعلى صلاحية.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const doCreate = async () => {
    if (form.password !== form.password2) {
      alert('❌ كلمتا المرور غير متطابقتين');
      return;
    }
    if (form.password.length < 8) {
      alert('❌ كلمة المرور يجب أن تكون 8 أحرف على الأقل');
      return;
    }
    if (form.backup_type === 'season' && !form.season) {
      alert('❌ اختر الموسم أولاً');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/backup/create`, {
        backup_type: form.backup_type,
        password: form.password,
        season: form.backup_type === 'season' ? form.season : null,
        notes: form.notes || null,
      }, auth());
      alert(
        `✅ تمّ إنشاء النسخة بنجاح\n\n` +
        `📁 ${r.data.filename}\n` +
        `📦 الحجم: ${fmtSize(r.data.size_bytes)}\n` +
        `📊 ${r.data.documents.toLocaleString()} وثيقة\n` +
        `🔒 SHA-256: ${r.data.sha256.slice(0, 16)}...\n\n` +
        `⚠️ احفظ كلمة المرور — لا يمكن استرجاعها!`
      );
      setCreateOpen(false);
      setForm({ backup_type: 'full', password: '', password2: '', season: '', notes: '' });
      await fetchAll();
    } catch (e) {
      alert(`❌ فشل: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const doDownload = async (filename) => {
    try {
      const res = await axios.get(`${API}/backup/download/${encodeURIComponent(filename)}`, {
        ...auth(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // Refresh download counter
      setTimeout(fetchAll, 500);
    } catch (e) {
      alert(`❌ فشل التحميل: ${e.response?.data?.detail || e.message}`);
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/backup/${encodeURIComponent(confirmDel.filename)}`, auth());
      setConfirmDel({ open: false, filename: '' });
      await fetchAll();
    } catch (e) {
      alert(`❌ ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="p-6 space-y-6" dir="rtl" data-testid="secure-backup-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7 text-brand-600" />
            🛡️ مركز النسخ الآمن (Backup & Secure Archive Center)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            نسخ مضغوطة ومشفّرة AES-256 محميّة بكلمة مرور — قابلة للفتح في أيّ نظام (7-Zip / Keka / unzip)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} data-testid="refresh-backups">
            <RefreshCw className="h-4 w-4 ml-2" />
            تحديث
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-brand-600 hover:bg-brand-700 text-white"
            data-testid="open-create-modal"
          >
            <Plus className="h-4 w-4 ml-2" />
            إنشاء نسخة احتياطية
          </Button>
        </div>
      </div>

      {/* Stats dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DashCard color="blue" icon={<Database className="h-7 w-7" />}
          label="إجمالي النسخ" value={stats?.total ?? '...'} />
        <DashCard color="green" icon={<HardDrive className="h-7 w-7" />}
          label="الحجم الإجمالي" value={fmtSize(stats?.total_size_bytes || 0)} />
        <DashCard color="purple" icon={<Lock className="h-7 w-7" />}
          label="نسخ مشفّرة" value={`${stats?.encrypted_count ?? 0} / ${stats?.total ?? 0}`} />
        <DashCard color="amber" icon={<History className="h-7 w-7" />}
          label="آخر نسخة"
          value={stats?.last_backup?.created_at ? formatDateWithEnglishNumerals(stats.last_backup.created_at).split(' ')[0] : '-'} />
      </div>

      {/* By type breakdown */}
      {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-semibold text-gray-600">توزيع النسخ:</span>
              {Object.entries(stats.by_type).map(([t, c]) => {
                const cfg = TYPE_BADGES[t] || { label: t, color: 'bg-gray-100', icon: '•' };
                return (
                  <Badge key={t} className={`${cfg.color} text-sm py-1 px-3`}>
                    {cfg.icon} {cfg.label}: <span className="font-bold mr-1">{c}</span>
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs: Backups list + Audit log */}
      <Tabs defaultValue="backups">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="backups" data-testid="tab-backups-list">
            <Database className="h-4 w-4 ml-2" />
            📁 سجل النسخ ({backups.length})
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <FileCheck className="h-4 w-4 ml-2" />
            📋 سجل التدقيق
          </TabsTrigger>
        </TabsList>

        {/* Backups list */}
        <TabsContent value="backups">
          <Card>
            <CardContent className="p-0">
              <Table data-table-id="backups-list">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">اسم الملف</TableHead>
                    <TableHead className="text-right">النوع</TableHead>
                    <TableHead className="text-right">الحجم</TableHead>
                    <TableHead className="text-right">الوثائق</TableHead>
                    <TableHead className="text-right">SHA-256</TableHead>
                    <TableHead className="text-right">تنزيلات</TableHead>
                    <TableHead className="text-right">التاريخ</TableHead>
                    <TableHead className="text-right">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8">⏳ جاري التحميل...</TableCell></TableRow>
                  ) : backups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                        <Database className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                        لا توجد نسخ بعد. اضغط "إنشاء نسخة احتياطية" للبدء.
                      </TableCell>
                    </TableRow>
                  ) : backups.map((b) => {
                    const cfg = TYPE_BADGES[b.backup_type] || { label: b.backup_type, color: 'bg-gray-100', icon: '•' };
                    return (
                      <TableRow key={b.filename} data-testid={`backup-row-${b.filename}`}>
                        <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                        <TableCell>
                          <Badge className={cfg.color}>{cfg.icon} {cfg.label}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">{fmtSize(b.size_bytes)}</TableCell>
                        <TableCell><Badge variant="outline">{b.documents?.toLocaleString()}</Badge></TableCell>
                        <TableCell className="font-mono text-[10px] text-gray-600" title={b.sha256}>
                          {b.sha256?.slice(0, 16)}...
                        </TableCell>
                        <TableCell>{b.download_count || 0}</TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {formatDateWithEnglishNumerals(b.created_at)}
                          <div className="text-gray-400">{b.created_by_name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => doDownload(b.filename)}
                              className="text-brand-600 hover:bg-brand-50"
                              title="تحميل"
                              data-testid={`download-${b.filename}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDel({ open: true, filename: b.filename })}
                              className="text-red-600 hover:bg-red-50"
                              title="حذف"
                              data-testid={`delete-${b.filename}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit log */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>📋 سجل التدقيق</CardTitle>
              <CardDescription>كل إنشاء أو تنزيل أو حذف للنسخ الاحتياطية مسجّل هنا.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">الإجراء</TableHead>
                    <TableHead className="text-right">المنفّذ</TableHead>
                    <TableHead className="text-right">الملف</TableHead>
                    <TableHead className="text-right">التاريخ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-500">لا توجد إجراءات بعد</TableCell></TableRow>
                  ) : audit.map((a, i) => (
                    <TableRow key={i} data-testid={`audit-row-${i}`}>
                      <TableCell>{auditLabel(a.action)}</TableCell>
                      <TableCell className="text-sm">{a.by_name || '-'}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">{a.filename || '-'}</TableCell>
                      <TableCell className="text-xs">{formatDateWithEnglishNumerals(a.at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> إنشاء نسخة احتياطية آمنة
            </DialogTitle>
            <DialogDescription>
              النسخة ستُحفظ كملف <code>.secure.zip</code> مشفّر AES-256 محمي بكلمة مرور.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>نوع النسخة</Label>
              <Select value={form.backup_type} onValueChange={(v) => setForm({ ...form, backup_type: v })}>
                <SelectTrigger data-testid="backup-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">💾 نسخة كاملة (كل قواعد البيانات)</SelectItem>
                  <SelectItem value="season">📅 نسخة موسم (موسم محدد)</SelectItem>
                  <SelectItem value="financial">💰 نسخة مالية (عمليات + فواتير + دفعات)</SelectItem>
                  <SelectItem value="archive_export">🗄️ تصدير الأرشيف (المؤرشف فقط)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.backup_type === 'season' && (
              <div>
                <Label>الموسم</Label>
                <Select value={form.season} onValueChange={(v) => setForm({ ...form, season: v })}>
                  <SelectTrigger data-testid="season-select"><SelectValue placeholder="اختر الموسم..." /></SelectTrigger>
                  <SelectContent>
                    {seasons.map((s) => (
                      <SelectItem key={s.season} value={s.season}>
                        {s.season} ({s.count} عملية — {s.total_amount?.toLocaleString()} دج)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-600" /> كلمة المرور (8+ أحرف)
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                data-testid="backup-password-1"
                autoFocus
              />
            </div>
            <div>
              <Label>تأكيد كلمة المرور</Label>
              <Input
                type="password"
                value={form.password2}
                onChange={(e) => setForm({ ...form, password2: e.target.value })}
                placeholder="••••••••"
                data-testid="backup-password-2"
              />
              {form.password && form.password2 && form.password !== form.password2 && (
                <p className="text-xs text-red-600 mt-1">⚠️ كلمتا المرور غير متطابقتين</p>
              )}
            </div>
            <div>
              <Label>ملاحظات (اختياري)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="مثلاً: نسخة قبل قفل موسم العمرة 2025"
                data-testid="backup-notes"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <strong>تحذير:</strong> احفظ كلمة المرور في مكان آمن.
                لا يمكن استرجاعها لاحقاً، وبدونها لا يمكن فتح الملف.
              </span>
            </div>
          </div>

          <DialogFooter className="flex flex-row-reverse gap-2">
            <Button
              onClick={doCreate}
              disabled={creating || !form.password || form.password !== form.password2}
              className="bg-brand-600 hover:bg-brand-700 text-white"
              data-testid="confirm-create-backup"
            >
              <Database className="h-4 w-4 ml-2" />
              {creating ? '⏳ جاري الإنشاء...' : '🔒 إنشاء النسخة'}
            </Button>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmDel.open} onOpenChange={(o) => !o && setConfirmDel({ open: false, filename: '' })}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-red-700">🗑️ تأكيد الحذف</DialogTitle>
            <DialogDescription>
              هل أنت متأكد من حذف الملف <strong className="font-mono">{confirmDel.filename}</strong>؟
              <br /><br />
              ⚠️ سيتم تسجيل الحذف في سجلّ التدقيق ولن يمكن استرجاع الملف.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row-reverse gap-2">
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete">
              نعم، احذف
            </Button>
            <Button variant="outline" onClick={() => setConfirmDel({ open: false, filename: '' })}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DashCard = ({ color, icon, label, value }) => {
  const colors = {
    blue: 'from-brand-500 to-brand-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-violet-600',
    amber: 'from-amber-500 to-orange-600',
  };
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div className="text-2xl font-bold text-gray-800">{value}</div>
          </div>
          <div className={`p-3 rounded-lg bg-gradient-to-br ${colors[color]} text-white shadow-md`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const auditLabel = (a) => {
  const map = {
    backup_create: <span className="text-green-700">💾 إنشاء نسخة</span>,
    backup_download: <span className="text-brand-700">⬇️ تنزيل</span>,
    backup_delete: <span className="text-red-700">🗑️ حذف</span>,
    backup_delete_attempt_denied: <span className="text-red-900">🚫 محاولة حذف مرفوضة</span>,
    backup_integrity_failed: <span className="text-rose-700">⚠️ فشل تحقق السلامة</span>,
  };
  return map[a] || <span className="text-gray-700">{a}</span>;
};

export default SecureBackupCenter;
